<template>
  <div class="m-2">
    <h1 class="mainheader d-flex justify-content-left p-2">
      <span>GO Term Enrichment for Plants</span>
    </h1>

    <div class="pl-2 text-left">
      <p>
        <b>Statistical Over/Under Representation (powered by
          <a href="https://pantherdb.org/" target="_blank" rel="noopener"
            >PANTHER<span class="sr-only"> (opens in a new tab)</span></a
          >)</b>
      </p>
      <p>
        Use this tool to identify
        <a href="https://geneontology.org/" target="_blank" rel="noopener"
          >Gene Ontology<span class="sr-only"> (opens in a new tab)</span></a
        >
        terms that are over or under-represented in a set of genes (for example
        from co-expression or RNAseq data). The data are sent to
        <a href="https://pantherdb.org/" target="_blank" rel="noopener"
          >the PANTHER Classification System<span class="sr-only">
            (opens in a new tab)</span
          ></a
        >
        which contains up to date GO annotation data for Arabidopsis and other
        plant species. Choose the advanced setting if you want to change
        parameters or explore PANTHER's other tools for analyzing sets of genes.
        <a :href="helpUrl" target="_blank" rel="noopener"
          >[Help]<span class="sr-only"> (opens in a new tab)</span></a
        >
      </p>
    </div>

    <div class="container p-2 border rounded shadow-sm form-bg">
      <form
        ref="form"
        method="POST"
        :action="pantherUrl"
        target="_blank"
        enctype="application/x-www-form-urlencoded"
        @submit="handleSubmit"
      >
        <fieldset class="border border-dark rounded p-2">
          <legend class="fieldset-title">Gene list</legend>
          <div class="form-group row">
            <label for="identifiers" class="col-sm-4 col-form-label text-left"
              >Enter a list of
              <a
                href="https://pantherdb.org/tips/tips_batchIdSearch_supportedId.jsp"
                target="_blank"
                rel="noopener"
                >valid identifiers<span class="sr-only">
                  (opens in a new tab)</span
                ></a
              >, separated by newline:</label
            >
            <div class="col-sm-8 text-left">
              <textarea
                id="identifiers"
                v-model="input"
                name="input"
                class="form-control"
                rows="10"
                :aria-describedby="errorMessage ? 'inputError' : null"
                :aria-invalid="inputInvalid ? 'true' : null"
              ></textarea>
              <button
                type="button"
                class="btn btn-link p-0 mt-1"
                :disabled="loadingSample"
                @click="loadSample"
              >
                Try a sample gene list
              </button>
              <div
                v-if="errorMessage"
                id="inputError"
                class="text-danger mt-1"
                role="alert"
              >
                {{ errorMessage }}
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset class="border border-dark rounded p-2 mt-4">
          <legend class="fieldset-title">Options</legend>
          <div class="form-group row">
            <label for="species" class="col-sm-4 col-form-label text-left"
              >Choose organism:</label
            >
            <div class="col-sm-8">
              <select
                id="species"
                v-model="species"
                name="species"
                class="form-control"
              >
                <option
                  v-for="option in speciesOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="ontology" class="col-sm-4 col-form-label text-left"
              >Choose GO aspect:</label
            >
            <div class="col-sm-8">
              <select
                id="ontology"
                v-model="ontology"
                name="ontology"
                class="form-control"
              >
                <option value="biological_process">biological process</option>
                <option value="molecular_function">molecular function</option>
                <option value="cellular_component">cellular component</option>
              </select>
            </div>
          </div>
        </fieldset>

        <input type="hidden" name="correction" value="fdr" />
        <input type="hidden" name="format" value="html" />
        <input type="hidden" name="resource" value="PANTHER" />

        <div class="form-group row">
          <div class="col-sm-8 offset-sm-1 d-flex align-items-center mt-3">
            <button type="submit" class="btn btn-primary">Submit</button>
            <span class="ml-4"
              >Or use
              <a href="https://pantherdb.org/" target="_blank" rel="noopener"
                >Advanced settings at PANTHER<span class="sr-only">
                  (opens in a new tab)</span
                ></a
              ></span
            >
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: "GoTermEnrichment",
  data() {
    return {
      pantherUrl: "https://pantherdb.org/webservices/go/overrep.jsp",
      helpUrl: "https://geneontology.org/docs/go-enrichment-analysis/",
      input: "",
      species: "ARATH",
      ontology: "biological_process",
      errorMessage: "",
      inputInvalid: false,
      loadingSample: false,
      speciesOptions: [
        { value: "ARATH", text: "Arabidopsis thaliana" },
        { value: "BRADI", text: "False Brome (Brachypodium distachyon)" },
        { value: "CHLRE", text: "Chlamydomonas reinhardtii" },
        { value: "SOYBN", text: "Soybean (Glycine max)" },
        { value: "ORYSJ", text: "Rice (Oryza sativa)" },
        { value: "PHYPA", text: "Moss (Physcomitrella patens)" },
        { value: "POPTR", text: "Poplar (Populus trichocarpa)" },
        { value: "SOLLC", text: "Tomato (Solanum lycopersicum)" },
        { value: "MAIZE", text: "Maize (Zea mays)" },
        { value: "VITVI", text: "Grape (Vitis vinifera)" },
        { value: "AMBTC", text: "Amborella (Amborella trichopoda)" },
        {
          value: "BRACM",
          text: "Chinese cabbage (Brassica rapa subsp. pekinensis)",
        },
        { value: "CITSI", text: "Orange (Citrus sinensis)" },
        { value: "CUCSA", text: "Cucumber (Cucumis sativus)" },
        { value: "HELAN", text: "Sunflower (Helianthus annuus)" },
        { value: "HORVV", text: "Barley (Hordeum vulgare subsp. vulgare)" },
        { value: "MEDTR", text: "Barrelclover (Medicago truncatula)" },
        {
          value: "MUSAM",
          text: "Banana (Musa acuminata subsp. malaccensis)",
        },
        { value: "TOBAC", text: "Tobacco (Nicotiana tabacum)" },
        { value: "PRUPE", text: "Peach (Prunus persica)" },
        { value: "RICCO", text: "Castor bean (Ricinus communis)" },
        { value: "SETIT", text: "Foxtail millet (Setaria italica)" },
        { value: "SORBI", text: "Sorghum (Sorghum bicolor)" },
        { value: "THECC", text: "Cocoa (Theobroma cacao)" },
        { value: "WHEAT", text: "Wheat (Triticum aestivum)" },
      ],
    };
  },
  methods: {
    handleSubmit(event) {
      if (!this.input.trim()) {
        event.preventDefault();
        this.errorMessage = "Enter at least one identifier.";
        this.inputInvalid = true;
        return;
      }
      this.errorMessage = "";
      this.inputInvalid = false;
    },
    async loadSample() {
      this.loadingSample = true;
      this.errorMessage = "";
      this.inputInvalid = false;
      const inputAtStart = this.input;
      try {
        const response = await fetch(
          `${process.env.BASE_URL}GoTermEnrichmentSample.txt`
        );
        if (!response.ok) throw new Error(response.statusText);
        const sample = await response.text();
        // Keep anything the user typed while the sample was loading.
        if (this.input === inputAtStart) this.input = sample;
      } catch (e) {
        this.errorMessage = "The sample gene list could not be loaded.";
      } finally {
        this.loadingSample = false;
      }
    },
  },
};
</script>

<style scoped>
.mainheader {
  font-size: 16pt;
  font-weight: bold;
  color: #930;
  margin: 0;
}
.fieldset-title {
  font-size: 1rem;
  font-weight: bold;
  width: auto;
  text-align: left;
  padding: 0 0.25rem;
  margin: 0 0 0.75rem 1.25rem;
}
</style>
